import React from "react"
import { Outlet, Navigate } from "react-router-dom"

// Layouts
import { Dashboard } from "@layouts/Dashboard"

// Guards
import { AuthenticationGuard } from "@components/guards/AuthenticationGuard"
import { RoleGuard } from "@components/guards/RoleGuard"
import {
  GENERAL_ROUTES_GROUP,
  ADMIN_OPS_ROUTES_GROUP,
  CASE_ROUTES_GROUP,
} from "@hooks/useHasPermissions"

// Pages
import Member from "@pages/membership"
import Offers from "@pages/offers"
import NewOrder from "@pages/prospect/NewOrder"
import OrderConfirmation from "@pages/prospect/OrderConfirmation"
import OrderVerification from "@pages/prospect/OrderVerification"
import OrderVerificationSteps from "@pages/prospect/OrderVerificationSteps"
import CasePage from "@pages/case/CasePage"
import MassNotate from "@pages/MassNotate"
import { AgedAccounts } from "@pages/AgedAccounts"
import { EditAgedAccount } from "@pages/AgedAccounts/EditAgedAccount"
import { FailedOrders } from "@pages/FailedOrders"
import { EditOrderPage } from "@pages/FailedOrders/EditOrder"
import { AllRewards } from "@pages/Rewards"
import { VendorRewards } from "@pages/Rewards/VendorRewards"
import { PendingRewards } from "@pages/Rewards/PendingRewards"
import { AccountPage } from "@pages/AccountPage"
import { ProspectPage } from "@pages/ProspectPage"
import { NewOrderPage } from "@pages/NewOrderPage/NewOrderPage"
import { SearchV2 } from "@pages/SearchV2"
import { ProspectCreate } from "@pages/ProspectCreate"
import { CasesV2 } from "@pages/CasesV2"
import Cases from "@pages/cases"

// Auth
import Page404 from "@pages/auth/Page404"
import { Callback } from "@pages/auth/Callback"

const renderRoute = (children, allowedRoles = GENERAL_ROUTES_GROUP) => {
  return <RoleGuard allowedRoles={allowedRoles}>{children}</RoleGuard>
}

const routes = [
  {
    path: "/",
    element: <AuthenticationGuard component={Dashboard} />,
    children: [
      {
        path: "/",
        element: renderRoute(<SearchV2 />),
      },
      {
        path: "/search",
        element: renderRoute(<SearchV2 />),
      },
      {
        path: "/prospect",
        element: renderRoute(<Outlet />),
        children: [
          {
            index: true, // This route is not enabled, redirects to home
            element: <Navigate to="/" replace />,
          },
          {
            path: "create",
            element: <ProspectCreate />,
          },
          {
            path: ":prospect_guid",
            element: <ProspectPage />,
          },
          {
            path: ":prospect_guid/new_order",
            element: <NewOrderPage />,
          },
        ],
      },
      {
        path: "/order/:shopper_short_key/offers",
        element: renderRoute(<Offers />),
      },
      {
        path: "/order/:shopper_short_key/join",
        element: renderRoute(<OrderVerification />),
      },
      {
        path: "/order/:shopper_short_key/verify",
        element: renderRoute(<OrderVerificationSteps />),
      },
      {
        path: "/order/:shopper_short_key/confirm",
        element: renderRoute(<OrderVerificationSteps />),
      },
      {
        path: "/order/:shopper_short_key/final",
        element: renderRoute(<OrderConfirmation />),
      },
      {
        path: "/profiles/:membership_profile_id/accounts/:account_id",
        element: renderRoute(<AccountPage />),
      },
      {
        path: "/profiles/:membership_profile_id",
        element: renderRoute(<Member />),
      },
      {
        path: "/order",
        element: renderRoute(<NewOrder />),
      },
      {
        path: "/cases_old",
        element: renderRoute(<Cases />, CASE_ROUTES_GROUP),
      },
      {
        path: "/cases",
        element: renderRoute(<CasesV2 />, CASE_ROUTES_GROUP),
      },
      {
        path: "/cases/:case_guid",
        element: renderRoute(<CasePage />, CASE_ROUTES_GROUP),
      },
      {
        path: "/ops/mass_notate",
        element: renderRoute(<MassNotate />, ADMIN_OPS_ROUTES_GROUP),
      },
      {
        path: "/ops/aged_accounts",
        element: renderRoute(<Outlet />, ADMIN_OPS_ROUTES_GROUP),
        children: [
          {
            index: true,
            element: <AgedAccounts />,
          },
          {
            path: ":account_id",
            element: <EditAgedAccount />,
          },
        ],
      },
      {
        path: "/ops/orders/failed",
        element: renderRoute(<Outlet />, ADMIN_OPS_ROUTES_GROUP),
        children: [
          {
            index: true,
            element: <FailedOrders />,
          },
          {
            path: ":order_id",
            element: <EditOrderPage />,
          },
        ],
      },
      {
        path: "/ops/rewards",
        element: renderRoute(<Outlet />, ADMIN_OPS_ROUTES_GROUP),
        children: [
          {
            index: true,
            element: <AllRewards />,
          },
          {
            path: ":fulfillment_vendor_code",
            element: <VendorRewards />,
          },
          {
            path: ":fulfillment_vendor_code/:fulfillment_channel",
            element: <PendingRewards />,
          },
        ],
      },
    ],
  },
  {
    path: "callback",
    element: <Callback />,
  },
  {
    path: "*",
    element: <Page404 />,
  },
  {
    path: "private",
    element: <AuthenticationGuard component={Dashboard} />,
  },
]

export default routes
